import { createAsyncThunk } from "@reduxjs/toolkit";
import type { IHighlight } from "@argument-studio/react-pdf-highlighter-with-categories";
import { DEFAULT_CATEGORIES } from "@shared/utils/defaultCategories";
import { setIsDemoMode } from "@store/slices/viewSlice";
import {
  setWorkspaceData,
  setReadings,
  setReadingHighlights,
  setCanvases,
  setCanvasData,
  setPapers,
  setSelectedReading,
  setSelectedCanvas,
} from "@store/slices/workspaceSlice";
import type { ReadingWithUserStatus, Canvas } from "@/types/models";
import type { CanvasState } from "@/types/canvasTypes";
import type { CanvasData } from "@/types/workspace";

const DEMO_WORKSPACE_ID = "demo-workspace";
const DEMO_READING_ID = "demo-reading";
const DEMO_CANVAS_ID = "demo-canvas";
const DEMO_DOCUMENT_ID = "demo-document";
const DEMO_PDF_PATH = "/demo/demo.pdf";
const DEMO_HIGHLIGHTS_PATH = "/demo/highlights.json";
const DEMO_CANVAS_PATH = "/demo/canvas.json";

const fetchDemoJson = async <T>(path: string, fallback: T): Promise<T> => {
  try {
    const res = await fetch(path);
    if (!res.ok) return fallback;
    return (await res.json()) as T;
  } catch {
    return fallback;
  }
};

export const bootstrapDemoSession = createAsyncThunk(
  "demo/bootstrap",
  async (_: void, { dispatch }) => {
    dispatch(setIsDemoMode(true));

    const [highlights, canvasData] = await Promise.all([
      fetchDemoJson<IHighlight[]>(DEMO_HIGHLIGHTS_PATH, []),
      fetchDemoJson<CanvasState>(DEMO_CANVAS_PATH, {
        rectangles: [],
        scale: 1,
        label: "Demo Canvas",
        tag: "demo-canvas",
      }),
    ]);

    dispatch(
      setWorkspaceData({
        workspace_id: DEMO_WORKSPACE_ID,
        workspace_name: "Demo Workspace",
        shared_data: { categories: [...DEFAULT_CATEGORIES] },
        user_data: { timeLog: 0 },
        workspace_permissions: [],
      })
    );

    const demoReading = {
      reading_id: DEMO_READING_ID,
      workspace_id: DEMO_WORKSPACE_ID,
      document_id: DEMO_DOCUMENT_ID,
      document_name: "Demo Paper",
      document_url: DEMO_PDF_PATH,
      url_expiration_date: null,
      description: null,
      instructions: null,
      due_date: null,
      required: false,
      collaboration_enabled: false,
      created_at: new Date().toISOString(),
      updated_at: null,
      reading_data: { highlights: [] },
      status: null,
      annotation_count: 0,
    } as unknown as ReadingWithUserStatus;

    dispatch(setReadings([demoReading]));
    dispatch(
      setReadingHighlights({
        readingId: DEMO_READING_ID,
        highlights,
      })
    );

    const demoCanvas = {
      canvas_id: DEMO_CANVAS_ID,
      workspace_id: DEMO_WORKSPACE_ID,
      canvas_name: "Demo Canvas",
      canvas_data: "",
      created_at: new Date().toISOString(),
      updated_at: null,
    } as unknown as Canvas;

    dispatch(setCanvases([demoCanvas]));
    dispatch(
      setCanvasData({
        canvasId: DEMO_CANVAS_ID,
        canvasData: canvasData as unknown as CanvasData,
      })
    );
    dispatch(setPapers([]));

    dispatch(setSelectedReading(DEMO_READING_ID));
    dispatch(setSelectedCanvas(DEMO_CANVAS_ID));
  }
);

export const DEMO_IDS = {
  workspaceId: DEMO_WORKSPACE_ID,
  readingId: DEMO_READING_ID,
  canvasId: DEMO_CANVAS_ID,
  documentId: DEMO_DOCUMENT_ID,
  pdfPath: DEMO_PDF_PATH,
};
